import React, { useState, useEffect, forwardRef } from 'react';
import {
  fieldService,
  attributeRoleService,
  attributeSensitivityService,
  attributeUnitService,
  attributeValueService,
  entityService,
  entityTypeService,
  glossaryService,
} from '../services';
import {
  AttributeValuesForm,
  Checkbox,
  CustomPropertiesForm,
  FormTabs,
  Select,
  SelectButtons,
  SelectOwner,
  formatSelectOptions,
  Icon,
  InputStatus,
  Textarea,
  textareaInitialState,
  textareaToRaw,
  textareaToPlainText,
  attributeValueDescription,
} from '../components';
import {
  Asset,
  AssetName,
  AttributeRole,
  AttributeSensitivity,
  canHaveAttributeValues,
  fetchAssetsWithSameName,
  fetchAttributeValues as fetchAttributeValuesForAttribute,
  formatPath,
  getBatchEditValue,
  getHierarchyPath,
  getSameNameValidation,
} from '../helpers';
import {
  useCustomProperties,
  useDebounce,
  useGlobalState,
  useSearch,
  useTooltip,
  useMessage,
} from '../hooks';

/**
 * One form for both kinds of glossary asset.
 *
 * A glossary holds Entities and Attributes. They are separate tables, but a user should not have to
 * know that: they pick a role — Term, Dimension or Measure — and the form decides where the asset
 * belongs. Term means an Entity; Dimension and Measure are roles of an Attribute.
 *
 * The role picker's value is therefore either the sentinel `Asset.Entity` (the string 'entity') or an
 * `attribute_role_id` (an integer), so the two can never be confused.
 *
 * **Naming.** Three vocabularies meet in this file, and telling them apart is what keeps it readable:
 *
 *   `asset_*`      form state for a property both kinds have — `asset_name`, `asset_description`,
 *                  `asset_role_id`. Deliberately not a column name in either table, because the two
 *                  kinds spell these differently; the input `name` attributes match, so
 *                  handleInputChange writes straight into state.
 *   `attribute_*`  a property only an Attribute has, in form state and in the database alike —
 *                  `attribute_definition`, `attribute_sensitivity_id`, `attribute_unit_id`, `is_pii`.
 *   `entity_*`     likewise for an Entity: `entity_type_id`.
 *
 * Names that are the same on both tables — `glossary_id`, `parent_entity_id`, `status_id`,
 * `owner_principal_id` — are left as they are.
 *
 * Outside form state those prefixes mean the database column and must not be renamed: rows read from
 * the API (`data[0].attribute_name`, `item.attribute_role_id`), the payload `shapeForTarget` builds,
 * the `display_name_code` a label is looked up by, and `formErrors` keys, which arrive from the
 * backend keyed by column.
 *
 * Editing an existing asset and choosing a role on the other side of that boundary converts it. The
 * form does not call the conversion itself; it reports the chosen target as `target_object_name` and,
 * for a conversion, the id it came from as `converted_from_entity_id` / `converted_from_attribute_id`.
 * The view's addAsset/editAsset picks the service from that.
 *
 * @param {object} props
 * @param {'add'|'add_child'|'edit'} props.mode
 * @param {object[]} props.data - Selected rows; entities carry entity_id, attributes attribute_id.
 * @param {Function} props.submitForm - Receives the payload; owned by the calling view.
 * @param {boolean} props.loading
 * @param {Function} props.setLoading
 * @param {Record<string, string>} [props.formErrors] - Field-keyed errors from a 400 response.
 * @param {'entity'|'attribute'} [props.defaultObjectName] - Which role the picker starts on when
 *   adding. Comes from the dialog key, so "Add Entity" opens on Term and "Add Attribute" on a role.
 */
const _AddGlossaryAssetForm = (
  {
    mode,
    data,
    submitForm,
    loading,
    setLoading,
    formErrors = {},
    defaultObjectName = Asset.Attribute,
  },
  ref
) => {
  const initialFormState = {
    attribute_id: null,
    entity_id: null,
    asset_name: '',
    asset_description: textareaInitialState({}),
    attribute_definition: textareaInitialState({}),
    is_pii: false,
    // Either Asset.Entity (this asset is a Term) or an attribute_role_id.
    asset_role_id: '',
    attribute_sensitivity_id: '',
    attribute_unit_id: '',
    glossary_id: null,
    parent_entity_id: null,
    entity_type_id: null,
    owner_principal_id: '',
    status_id: '',
  };

  const initialBatchEditItems = {
    batch_is_pii: false,
    batch_asset_role_id: false,
    batch_attribute_sensitivity_id: false,
    batch_attribute_unit_id: false,
    batch_glossary_id: false,
    batch_parent_entity_id: false,
    batch_entity_type_id: false,
    batch_owner_principal_id: false,
    batch_status_id: false,
  };

  const [loggedInUser] = useGlobalState('loggedInUser');
  const [statuses] = useGlobalState('statuses');
  const [currentGlossary] = useGlobalState('currentGlossary');
  const [formData, setFormData] = useState(initialFormState);
  const [batchEdit, setBatchEdit] = useState(false);
  const [batchEditItems, setBatchEditItems] = useState(initialBatchEditItems);
  const [fieldAssociations, setFieldAssociations] = useState([]);
  const [tmpFieldAssociations, setTmpFieldAssociations] = useState([]);
  const [attributeRoles, setAttributeRoles] = useState([]);
  const [attributeSensitivities, setAttributeSensitivities] = useState([]);
  const [attributeUnits, setAttributeUnits] = useState([]);
  const [assetsWithSameName, setAssetsWithSameName] = useState({ attributes: [], entities: [] });
  const [glossaries, setGlossaries] = useState([]);
  const [entities, setEntities] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);
  // What the attribute's values were when the form opened, and the working copy the Values tab
  // edits. The pristine list is what the role-change warning counts: it is the saved values a
  // change to Measure or Term would discard, not the ones staged here.
  const [attributeValues, setAttributeValues] = useState([]);
  const [tmpAttributeValues, setTmpAttributeValues] = useState([]);
  // Whether the values have been read. The payload key replaces every value the attribute has, so
  // an empty list must never be sent for an attribute whose values simply were not loaded.
  const [attributeValuesLoaded, setAttributeValuesLoaded] = useState(false);
  const [loadingMoreOptions, setLoadingMoreOptions] = useState(false);
  const [defaultSelectedTab, setDefaultSelectedTab] = useState();
  const [requestResults, setRequestResults] = useState([]);

  // `data` arrives populated in all three modes, but means something different in each:
  //   edit       — the asset(s) being changed;
  //   add        — the selected asset to copy, which is a deliberate "add another like this";
  //   add_child  — the PARENT of the new asset, which must not be copied, or the child would come out
  //                as a duplicate of its own parent.
  // So values are read from the selection in every mode except add_child.
  const readsSelection = mode !== 'add_child';

  // Whether an existing row is being updated. A copy is a new asset, so it carries no id and can
  // never be a conversion.
  const isEdit = mode !== 'add' && mode !== 'add_child';

  // Which kind the selection is. Rows carry one id or the other, never both.
  const selectionObjectName = data[0]?.attribute_id
    ? Asset.Attribute
    : data[0]?.entity_id
      ? Asset.Entity
      : null;

  // The kind the form is working from — null for add_child, where the selection is only the parent.
  const sourceObjectName = readsSelection ? selectionObjectName : null;

  // What the chosen role makes it. The picker holds Asset.Entity for Term, otherwise a role id.
  const isEntity = formData.asset_role_id === Asset.Entity;
  const targetObjectName = isEntity ? Asset.Entity : Asset.Attribute;

  // A unit only applies to a Measure, since that is the role whose values are quantified. Resolved
  // from the role's name rather than its id, which is a surrogate key. In a batch edit the role may
  // be 'multiple', meaning it is not being changed — the selection can still hold Measures, so the
  // unit stays available and is only hidden when the batch is unambiguously Dimension.
  const selectedRoleName = attributeRoles.find(
    role => role.attribute_role_id === formData.asset_role_id
  )?.attribute_role_name;
  const isMeasure = selectedRoleName?.toLowerCase() === AttributeRole.measure.toLowerCase();
  const showAttributeUnit = !isEntity && (isMeasure || formData.asset_role_id === 'multiple');

  // Only a Dimension is enumerated, so only a Dimension has values. Read from the role's name for
  // the same reason the unit is; 'multiple' in a batch edit is neither, which is right -- the tab
  // is hidden there anyway.
  const isDimension = selectedRoleName?.toLowerCase() === AttributeRole.dimension.toLowerCase();

  // Editing an existing asset and crossing the Term/role boundary moves it between tables.
  const isConversion = isEdit && !!sourceObjectName && sourceObjectName !== targetObjectName;

  /**
   * The display_name_code the label and tooltip of a shared field should be read from.
   *
   * A shared field is held under an `asset_*` name here, which is deliberately not a column name in
   * either table — the two kinds spell the same property differently. There is no display_name row
   * for `asset_description`, so a shared field has to name the row it reads, and which row that is
   * depends on the chosen role: a Term reads "Description of an entity", a Dimension or Measure
   * "Description of an attribute". Keeping it a lookup rather than a literal leaves the text
   * editable as reference data.
   *
   * @param {string} attributeCode - The attribute-side code, e.g. 'attribute_description'.
   * @returns {string} That code, or the entity-side equivalent when the asset is a Term.
   */
  const sharedFieldCode = attributeCode =>
    isEntity ? attributeCode.replace(/^attribute_/, 'entity_') : attributeCode;

  const { showMessage, hideMessage } = useMessage();

  const { showTooltip, hideTooltip } = useTooltip();
  const {
    searchTerm,
    searchResults,
    searchResultsCount,
    setSearchTerm,
    fetchAllSearchResults,
    clearSearchResults,
    loadNextPage,
  } = useSearch({
    setLoading: setLoadingMoreOptions,
    objectName: 'field',
    pagination: true,
    enableHistory: false,
  });
  const {
    customProperties,
    formCustomPropertyValues,
    batchEditFormCustomPropertyValues,
    batchEditItemsFormCustomPropertyValues,
    setFormCustomPropertyValues,
    hasCustomProperties,
    getCustomPropertyValuesForSubmit,
    resetFormCustomPropertyValues,
  } = useCustomProperties({
    // The asset's custom properties are those of the kind it currently is; for a new asset, of the
    // kind being created. Fixed for as long as the dialog is open, so it does not follow the picker.
    objectName: sourceObjectName || defaultObjectName,
    // Copying an asset copies its custom property values too. In add_child no ids are passed, since
    // there the selection is the parent and its values must not leak into the child.
    objectIds: readsSelection ? data.map(x => x.attribute_id ?? x.entity_id) : [],
  });

  useEffect(() => {
    setRequestResults(
      searchResults.map(result => {
        const selectedByOther =
          result.searchable.attribute_id &&
          result.searchable.attribute_id.toString() !== formData.attribute_id
            ? true
            : false;
        return {
          value: result.searchable.field_id,
          label: result.searchable.field_name,
          icon: result.searchable.datatype_category,
          secondaryLabel: formatPath([
            result.searchable.system_name,
            result.searchable.datasource_name,
            result.searchable.dataset_group_name,
            result.searchable.dataset_name,
          ]),
          selectedByOther: selectedByOther,
          tooltip: selectedByOther
            ? `This Field is already associated with Attribute:\n${result.searchable.attribute_name}`
            : '',
        };
      })
    );
  }, [searchResults]);

  useEffect(() => {
    // Get form data
    fetchAttributeRoles();
    fetchAttributeSensitivities();
    fetchAttributeUnits();
    fetchGlossaries();

    const glossaryId = data[0]?.glossary_id || currentGlossary?.glossary_id;
    fetchParentEntities(glossaryId);
    if (glossaryId) fetchEntityTypes(glossaryId);

    // Only an attribute has field associations and values of its own, and only when editing.
    // Deliberately not loaded for a copy: a field can be bound to just one attribute, so carrying the
    // associations over would move those fields off the asset being copied rather than duplicate them.
    // The value count is only used for the "changing to Term discards these" warning.
    if (isEdit && data.length === 1 && sourceObjectName === Asset.Attribute) {
      fetchFieldAssociations();
      fetchAttributeValues();
    }
  }, []);

  useEffect(() => {
    setBatchEdit(false);
    if (data.length === 1 && readsSelection) {
      const defaultAttributeRole = attributeRoles?.filter(
        x => x.attribute_role_name.toLowerCase() === AttributeRole.dimension.toLowerCase()
      );
      const defaultAttributeSensitivity = attributeSensitivities?.filter(x => x.is_default);

      // An entity has no role of its own, so its picker sits on the Term sentinel. The name and
      // description are read from whichever kind's columns are present. Only an attribute has a
      // definition, so an entity row leaves it empty.
      const isEntityRow = sourceObjectName === Asset.Entity;

      setFormData({
        // Only an edit targets the existing row. A copy leaves both ids null so it is created as a
        // new asset rather than writing over the one it was copied from.
        attribute_id: isEdit ? (data[0].attribute_id ?? null) : null,
        entity_id: isEdit ? (data[0].entity_id ?? null) : null,
        asset_name: (isEntityRow ? data[0].entity_name : data[0].attribute_name) || '',
        asset_description: textareaInitialState({
          value: isEntityRow ? data[0].entity_description : data[0].attribute_description,
        }),
        attribute_definition: textareaInitialState({
          value: isEntityRow ? undefined : data[0].attribute_definition,
        }),
        is_pii: data[0].is_pii,
        asset_role_id: isEntityRow
          ? Asset.Entity
          : data[0].attribute_role_id ||
            (defaultAttributeRole.length > 0 && defaultAttributeRole[0].attribute_role_id),
        attribute_sensitivity_id:
          data[0].attribute_sensitivity_id ||
          (defaultAttributeSensitivity.length > 0 &&
            defaultAttributeSensitivity[0].attribute_sensitivity_id),
        attribute_unit_id: data[0].attribute_unit_id,
        glossary_id: data[0].glossary_id,
        parent_entity_id: data[0].parent_entity_id,
        entity_type_id: data[0].entity_type_id || null,
        status_id: data[0].status_id,
        owner_principal_id: data[0].owner_principal_id,
      });

      if (data[0].glossary_id) fetchEntityTypes(data[0].glossary_id);
    } else if (data.length > 1 && isEdit) {
      setBatchEdit(true);

      setFormData({
        is_pii: getBatchEditValue(data, 'is_pii'),
        asset_role_id:
          sourceObjectName === Asset.Entity
            ? Asset.Entity
            : getBatchEditValue(data, 'attribute_role_id'),
        attribute_sensitivity_id: getBatchEditValue(data, 'attribute_sensitivity_id'),
        attribute_unit_id: getBatchEditValue(data, 'attribute_unit_id'),
        glossary_id: getBatchEditValue(data, 'glossary_id'),
        parent_entity_id: getBatchEditValue(data, 'parent_entity_id'),
        entity_type_id: getBatchEditValue(data, 'entity_type_id'),
        status_id: getBatchEditValue(data, 'status_id'),
        owner_principal_id: getBatchEditValue(data, 'owner_principal_id'),
      });
    } else if (mode === 'add_child') {
      // "Add child" is only offered on an entity, so the new asset starts under it in the same
      // glossary. Its own kind still comes from the picker.
      const defaultType = entityTypes.find(t => t.entity_type_is_default);

      setFormData({
        ...initialFormState,
        status_id: statuses.default_glossary_status_id,
        owner_principal_id: loggedInUser?.principal_id,
        glossary_id: data[0]?.glossary_id || currentGlossary?.glossary_id || null,
        parent_entity_id: data[0]?.entity_id || null,
        entity_type_id: defaultType ? defaultType.entity_type_id : null,
        asset_role_id: Asset.Entity,
        attribute_sensitivity_id:
          attributeSensitivities.find(x => x.is_default)?.attribute_sensitivity_id || '',
      });
      setFieldAssociations([]);
    } else if (mode === 'add') {
      const defaultAttributeRole = attributeRoles?.filter(
        x => x.attribute_role_name.toLowerCase() === AttributeRole.dimension.toLowerCase()
      );
      const defaultAttributeSensitivity = attributeSensitivities?.filter(x => x.is_default);

      const defaultType = entityTypes.find(t => t.entity_type_is_default);

      setFormData({
        ...formData,
        status_id: statuses.default_glossary_status_id,
        owner_principal_id: loggedInUser?.principal_id,
        glossary_id: currentGlossary?.glossary_id || null,
        entity_type_id: defaultType ? defaultType.entity_type_id : null,
        attribute_role_id:
          defaultObjectName === Asset.Entity
            ? Asset.Entity
            : (defaultAttributeRole.length > 0 && defaultAttributeRole[0].attribute_role_id) || '',
        attribute_sensitivity_id:
          (defaultAttributeSensitivity.length > 0 &&
            defaultAttributeSensitivity[0].attribute_sensitivity_id) ||
          '',
      });
      setFieldAssociations([]);
    } else {
      resetForm();
    }
  }, [mode, data, loggedInUser, currentGlossary, attributeRoles, attributeSensitivities]);

  const fetchAttributeRoles = async () => {
    attributeRoleService
      .getAll()
      .then(res => {
        res && setAttributeRoles(res.attribute_roles);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchAttributeSensitivities = async () => {
    attributeSensitivityService
      .getAll()
      .then(res => {
        if (res) {
          const tmp = res.attribute_sensitivities.map(x => ({
            ...x,
            className: AttributeSensitivity[x.attribute_sensitivity_level].className,
          }));
          setAttributeSensitivities(tmp);
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchAttributeUnits = async () => {
    attributeUnitService
      .getAll()
      .then(res => {
        res && setAttributeUnits(res.attribute_units);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  /**
   * Looks up assets that already use the name being typed, in every glossary.
   *
   * The severity is decided when rendering (see getSameNameValidation), because it depends on the role
   * currently chosen — which can change without the name changing, and must not cost another request.
   * The asset being edited is excluded from its own result.
   *
   * Debounced because this runs on every keystroke and makes two requests.
   *
   * @param {string} name
   */
  const { debounce: fetchAssetsByName } = useDebounce(
    name => {
      fetchAssetsWithSameName({
        name,
        excludeAttributeId: data[0]?.attribute_id,
        excludeEntityId: data[0]?.entity_id,
      })
        .then(setAssetsWithSameName)
        .catch(err => {
          showMessage(err, 'error');
        });
    },
    300,
    // Rebuilt when the selection changes, so the excluded ids never come from a previous dialog.
    [data]
  );

  const fetchGlossaries = async () => {
    glossaryService
      .getAll()
      .then(res => {
        res && setGlossaries(res.glossaries);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  /**
   * Loads the entities that may be chosen as a parent. Only entities can have children, so this list
   * is what "an attribute is always a leaf" looks like in the form.
   * @param {number} glossary_id
   */
  const fetchParentEntities = async glossary_id => {
    if (!glossary_id) {
      setEntities([]);
      return;
    }

    entityService
      .getByGlossaryId(glossary_id)
      .then(res => {
        if (res) {
          let result = res.entities;

          // An entity cannot be its own parent, so the rows being edited are removed. When adding,
          // nothing is excluded — the selected row, and the parent in "add child", are both valid
          // choices for the new asset.
          if (isEdit && data.length > 0) {
            result = result.filter(x => !data.find(y => y.entity_id === x.entity_id));
          }

          setEntities(
            result.map(x => ({
              ...x,
              entity_path: getHierarchyPath(x, res.entities, Asset.Entity),
            }))
          );
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchEntityTypes = async glossary_id => {
    if (!glossary_id) {
      setEntityTypes([]);
      return;
    }

    entityTypeService
      .getByGlossaryId(glossary_id)
      .then(res => {
        const types = res.entity_types || [];
        setEntityTypes(types);

        if (mode === 'add' || mode === 'add_child') {
          const defaultType = types.find(t => t.entity_type_is_default);
          if (defaultType) {
            setFormData(prev => ({ ...prev, entity_type_id: defaultType.entity_type_id }));
          }
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  /**
   * Reads the attribute's own values, for the Values tab to edit and for the role-change warning
   * to count.
   *
   * Read through the shared helper rather than attributeValueService.getByAttributeId, which
   * publishes to the breadcrumb subjects as a side effect -- moving the page's idea of where the
   * user is because a dialog opened.
   */
  const fetchAttributeValues = async () => {
    fetchAttributeValuesForAttribute({
      attributeIds: [data[0].attribute_id],
      toRow: value => value,
    })
      .then(values => {
        setAttributeValues(values);
        setTmpAttributeValues(values);
        setAttributeValuesLoaded(true);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchFieldAssociations = async () => {
    fieldService
      .getByAttributeId(data[0].attribute_id)
      .then(res => {
        if (res) {
          const fields = res.fields.map(field => ({
            field_id: field.field_id,
            field_name: field.field_name,
            attribute_id: data[0].attribute_id,
            icon: field.datatype_category,
            secondaryLabel: formatPath([
              field.system_name,
              field.datasource_name,
              field.dataset_group_name,
              field.dataset_name,
            ]),
          }));
          setFieldAssociations(fields);
          setTmpFieldAssociations(fields);
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const addFieldAssociation = async event => {
    let { value, label, icon, secondaryLabel } = '';

    value = event.value;
    label = event.option.label;
    icon = event.option.icon;
    secondaryLabel = event.option.secondaryLabel;

    const field = {
      field_id: value,
      attribute_id: formData.attribute_id,
      field_name: label,
      icon,
      secondaryLabel,
      isNew: true,
    };

    let newTmpFieldAssociations = [];
    if (tmpFieldAssociations.findIndex(x => x.field_id === value) > -1) {
      newTmpFieldAssociations = tmpFieldAssociations.filter(x => x.field_id !== value);
    } else {
      newTmpFieldAssociations = [...[field], ...tmpFieldAssociations];
    }

    setTmpFieldAssociations(newTmpFieldAssociations);
  };

  const removeFieldAssociation = item => {
    setTmpFieldAssociations(prev => prev.filter(x => x.field_id !== item.field_id));
  };

  const handleInputChange = (event, formPart) => {
    let { name, value } = '';

    // Handle standard form inputs
    if (event.target !== undefined) {
      name = event.target.name;
      value = event.target.value;

      // If input element is a checkbox, we cannot use "value"
      if (event.target.type === 'checkbox') {
        value = event.target.checked;
      }

      // Handle custom form inputs
    } else {
      name = event.name;
      value = event.value;
    }

    switch (name) {
      case 'asset_name':
        fetchAssetsByName(value);
        break;
      case 'asset_role_id':
        // Becoming an Entity means the entity type applies, and an asset that was not one has no type
        // yet. fetchEntityTypes only fills the default in when adding, so the same is done here for a
        // role change — including a conversion, where the asset is an existing row being edited.
        if (value === Asset.Entity && !formData.entity_type_id) {
          const defaultType = entityTypes.find(t => t.entity_type_is_default);
          if (defaultType) {
            setFormData(prev => ({ ...prev, entity_type_id: defaultType.entity_type_id }));
          }
        }
        break;
      case 'glossary_id':
        // The parent and type options belong to the chosen glossary, and a name is only taken
        // within one glossary, so all three are re-read when it changes.
        fetchParentEntities(value);
        fetchEntityTypes(value);
        // The name lookup itself is glossary-independent; only the severity changes, which is
        // recomputed on render from the new glossary_id.

        // A parent from the previous glossary is no longer a valid choice.
        setFormData(prev => ({ ...prev, parent_entity_id: null, entity_type_id: null }));
        break;
      case 'is_pii':
        // Get minimum sensitivity class for PII data
        const minAttributeSensitivityForPII = attributeSensitivities
          .filter(x => x.is_allowed_for_pii)
          .reduce(function (prev, curr) {
            return prev.attribute_sensitivity_level > curr.attribute_sensitivity_level
              ? prev
              : curr;
          });

        // Check currently selected sensitivity class and change to lowest allowed sensitivity class if current class is not allowed
        if (batchEdit) {
          let tmp = data;

          if (value) {
            tmp = data.map(item => {
              let attributeSensitivityId =
                formData.attribute_sensitivity_id || item.attribute_sensitivity_id;
              if (
                attributeSensitivities.find(
                  x => x.attribute_sensitivity_id === item.attribute_sensitivity_id
                ).attribute_sensitivity_level >
                minAttributeSensitivityForPII.attribute_sensitivity_level
              ) {
                attributeSensitivityId = minAttributeSensitivityForPII.attribute_sensitivity_id;
              }

              return {
                ...item,
                attribute_sensitivity_id: attributeSensitivityId,
              };
            });
          }

          if (
            !batchEditItems.batch_attribute_sensitivity_id ||
            (batchEditItems.batch_attribute_sensitivity_id &&
              attributeSensitivities.find(
                x => x.attribute_sensitivity_id === formData.attribute_sensitivity_id
              )?.attribute_sensitivity_level >
                minAttributeSensitivityForPII.attribute_sensitivity_level)
          ) {
            setFormData({
              attribute_sensitivity_id: getBatchEditValue(tmp, 'attribute_sensitivity_id'),
            });
          }
        } else {
          if (
            attributeSensitivities.find(
              x => x.attribute_sensitivity_id === formData.attribute_sensitivity_id
            ).attribute_sensitivity_level >
            minAttributeSensitivityForPII.attribute_sensitivity_level
          ) {
            const attributeSensitivityId = minAttributeSensitivityForPII.attribute_sensitivity_id;

            setFormData(prev => {
              return {
                ...prev,
                attribute_sensitivity_id: attributeSensitivityId,
              };
            });
          }
        }

        break;
      default:
        break;
    }

    switch (formPart) {
      case 'batch':
        setBatchEditItems({ ...batchEditItems, [name]: value });
        if (name === 'batch_is_pii' && !batchEditItems.batch_attribute_sensitivity_id && !value) {
          setFormData({
            attribute_sensitivity_id: getBatchEditValue(data, 'attribute_sensitivity_id'),
          });
        }
        break;
      default:
        setFormData(prev => {
          return { ...prev, [name]: value };
        });
    }

    // Hide errors on input change
    if (formErrors && formErrors[name]) {
      delete formErrors[name];
      hideMessage();
    }
  };

  const handleSearchFieldInputChange = value => {
    setSearchTerm(value);
  };

  const handleSelectAll = async () => {
    const data = await fetchAllSearchResults();

    let newTmpFieldAssociations = [];

    data.searchResults.forEach(result => {
      //const selectedByOther = ((result.searchable.attribute_id && result.searchable.attribute_id.toString() !== formData.attribute_id) ? true : false)

      //if (!selectedByOther) {

      const field = {
        field_id: result.searchable.field_id,
        attribute_id: formData.attribute_id,
        field_name: result.searchable.field_name,
        icon: result.searchable.datatype_category,
        secondaryLabel: formatPath([
          result.searchable.system_name,
          result.searchable.datasource_name,
          result.searchable.dataset_group_name,
          result.searchable.dataset_name,
        ]),
      };

      if (tmpFieldAssociations.findIndex(x => x.field_id === field.field_id) < 0) {
        newTmpFieldAssociations.push(field);
      }
      //}
    });

    if (newTmpFieldAssociations.length > 0) {
      setTmpFieldAssociations([...tmpFieldAssociations, ...newTmpFieldAssociations]);
    } else {
      newTmpFieldAssociations = [...tmpFieldAssociations];

      data.searchResults.forEach(result => {
        newTmpFieldAssociations = newTmpFieldAssociations.filter(x => {
          return x.field_id !== result.searchable.field_id;
        });
      });
      setTmpFieldAssociations([...newTmpFieldAssociations]);
    }

    return data;
  };

  /**
   * The entity type to fall back on when a row becomes an entity without one — a conversion from an
   * attribute, which has no type to bring, or a batch edit, which reads each row's own values.
   *
   * Only used where the row carries no entity_type_id at all. A row whose type is explicitly null had it
   * cleared by the user, and clearing has to be possible: the type is optional, and an entity without one
   * reads as "Term".
   *
   * Entity types belong to a glossary, and the loaded list is the one for the assets being edited, so
   * the default is only applied when they all share a glossary. Editing assets from several glossaries
   * at once leaves the type unset — a type from the wrong glossary would be rejected by the API, and
   * guessing per row is not possible from one loaded list.
   *
   * @returns {number|null} The glossary's default entity_type_id, or null when it cannot be resolved.
   */
  const _defaultEntityTypeId = () => {
    if (!formData.glossary_id || formData.glossary_id === 'multiple') {
      return null;
    }

    return entityTypes.find(t => t.entity_type_is_default)?.entity_type_id ?? null;
  };

  /**
   * Renames the shared properties onto the chosen kind's column names, and marks a conversion.
   *
   * The form holds one set of shared values under the attribute_* names because that is the majority
   * case; this is where they become entity_* when Term is chosen. `target_object_name` tells the
   * calling view which service to use, and is not a column — the view strips it.
   *
   * Every property is picked explicitly rather than spread, so a value belonging to one kind can
   * never reach the other: sending attribute_role_id to POST /entity would fail on a column that
   * does not exist.
   *
   * @param {object} v - All candidate values, keyed as the form holds them.
   * @param {number|null} sourceId - The id of the row being edited, or null when adding.
   * @returns {object} A row shaped for the target kind.
   */
  const shapeForTarget = (v, sourceId) => {
    const shared = {
      glossary_id: v.glossary_id,
      parent_entity_id: v.parent_entity_id ?? null,
      status_id: v.status_id,
      owner_principal_id: v.owner_principal_id,
    };

    const row = isEntity
      ? {
          entity_name: v.name,
          entity_description: v.description,
          // Absent means the row never had a type — a converted attribute, or a batch row of the other
          // kind — so the glossary's default applies. Null means the user cleared the picker, which must
          // be sent as null: comparing against undefined rather than using ?? or || is what keeps those
          // two apart.
          entity_type_id:
            v.entity_type_id === undefined ? _defaultEntityTypeId() : v.entity_type_id,
          ...shared,
        }
      : {
          attribute_name: v.name,
          attribute_description: v.description,
          attribute_definition: v.definition,
          is_pii: v.is_pii ?? false,
          attribute_role_id: v.asset_role_id,
          // An entity holds no sensitivity, so converting one to an attribute brings none along, and
          // batch edit reads each row's own value rather than the form's. The column is NOT NULL, so
          // fall back to the class marked as default — what a brand new attribute starts with.
          attribute_sensitivity_id:
            v.attribute_sensitivity_id ||
            attributeSensitivities.find(x => x.is_default)?.attribute_sensitivity_id,
          // Only a Measure is quantified, so a Dimension is saved without a unit whatever the form
          // last held. The API clears it as well, for a client that does not go through this form.
          attribute_unit_id: showAttributeUnit ? (v.attribute_unit_id ?? null) : null,
          ...shared,
        };

    // Batch edit leaves the rich-text properties alone, so drop the ones with no value rather than
    // sending undefined and blanking them.
    Object.keys(row).forEach(key => row[key] === undefined && delete row[key]);

    if (isConversion) {
      // A conversion creates a row in the other table, so the asset gets a new id and the old row is
      // named as the source instead.
      return {
        ...row,
        [`converted_from_${sourceObjectName}_id`]: sourceId,
        target_object_name: targetObjectName,
      };
    }

    return {
      ...row,
      ...(sourceId ? { [`${targetObjectName}_id`]: sourceId } : {}),
      target_object_name: targetObjectName,
    };
  };

  const onSubmit = event => {
    event.preventDefault();

    setLoading(true);

    // Form validation
    //if (!formData.user_fullname || !formData.user_username) return

    let dataToSubmit;

    if (batchEdit) {
      let key = '';
      let tmp = {};
      const objects = Object.entries(batchEditItems);

      for (let i = 0; i < objects.length; i++) {
        if (objects[i][1]) {
          key = objects[i][0].replace('batch_', '');
          tmp = { ...tmp, [key]: formData[key] };
        }
      }

      // Get minimum sensitivity class for PII data
      //const minAttributeSensitivityForPII = attributeSensitivities.filter(x => x.is_allowed_for_pii).reduce(function(prev, curr) {
      //														    return prev.attribute_sensitivity_level > curr.attribute_sensitivity_level ? prev : curr;
      //														})

      dataToSubmit = data.map(item => {
        /*let attributeSensitivityId = item.attribute_sensitivity_id

				if (tmp.attribute_sensitivity_id && !attributeSensitivities.find(x => x.attribute_sensitivity_id === tmp.attribute_sensitivity_id)?.is_allowed_for_pii) {

		  		attributeSensitivityId = attributeSensitivities.filter(x => x.is_allowed_for_pii).reduce(function(prev, curr) {
																	    return prev.attribute_sensitivity_level > curr.attribute_sensitivity_level ? prev : curr;
																	}).attribute_sensitivity_id
		  	}*/
        /*let attributeSensitivityId = tmp.attribute_sensitivity_id || item.attribute_sensitivity_id

				// Ensure minimum sensitivity class for PII data is set
				if (batchEditItems.batch_is_pii && // PII is edited
						tmp.is_pii && // PII is set to true
						!attributeSensitivities.find(x => x.attribute_sensitivity_id === item.attribute_sensitivity_id)?.is_allowed_for_pii // Current sensitivity class is lower than required for PII data
					) {
					attributeSensitivityId = minAttributeSensitivityForPII.attribute_sensitivity_id 
				}*/

        const sourceId = item.attribute_id ?? item.entity_id;
        const isEntityRow = sourceObjectName === Asset.Entity;

        // Each row keeps its own unedited values; `tmp` holds only the properties the user ticked
        // for batch editing. Names are read from whichever kind's column the row actually has.
        const row = {
          asset_name: isEntityRow ? item.entity_name : item.attribute_name,
          is_pii: item.is_pii,
          attribute_sensitivity_id: item.attribute_sensitivity_id,
          asset_role_id: isEntityRow ? Asset.Entity : item.attribute_role_id,
          attribute_unit_id: item.attribute_unit_id,
          glossary_id: item.glossary_id,
          parent_entity_id: item.parent_entity_id,
          entity_type_id: item.entity_type_id,
          status_id: item.status_id,
          owner_principal_id: item.owner_principal_id,
          ...tmp,
        };

        return {
          // Batch edit never exposes the rich-text properties, so name/description/definition/example
          // are left undefined here and dropped by shapeForTarget rather than blanked.
          ...shapeForTarget({ ...row, name: row.asset_name }, sourceId),
          ...getCustomPropertyValuesForSubmit(sourceId),
        };
      });
    } else {
      const sourceId = formData.attribute_id ?? formData.entity_id;

      dataToSubmit = {
        ...shapeForTarget(
          {
            ...formData,
            name: formData.asset_name,
            description: textareaToRaw({ value: formData.asset_description }),
            definition: textareaToRaw({ value: formData.attribute_definition }),
          },
          sourceId
        ),
        // Only an attribute can be bound to fields. Sending them for an entity would be meaningless,
        // and on a conversion to Term they are dropped by the server anyway.
        ...(isEntity
          ? {}
          : {
              field_associations: tmpFieldAssociations.map(field => ({
                field_id: field.field_id,
              })),
            }),
        // Only a Dimension has values, and the key replaces every value the attribute has -- so it
        // is sent only when this form is the one that read them, or when there is nothing to read
        // because the asset is new. A Measure or a Term sends nothing and leaves the server's own
        // handling in place: values are discarded along with the role that held them.
        ...(isDimension && (attributeValuesLoaded || !isEdit)
          ? {
              // A value marked for deletion is simply left out: the key replaces every value the
              // attribute has, so what is not named here is what the server deletes.
              attribute_values: tmpAttributeValues
                .filter(value => !value.isDeleted)
                .map(value => ({
                  attribute_value_id: value.attribute_value_id ?? null,
                  attribute_value_name: value.attribute_value_name,
                  attribute_value_code: value.attribute_value_code || null,
                  attribute_value_description: attributeValueDescription(value),
                  status_id: value.status_id,
                })),
            }
          : {}),
        ...getCustomPropertyValuesForSubmit(),
      };
    }

    submitForm(dataToSubmit)
      .then(() => resetForm())
      .catch(() => {
        // Errors handled in submitForm function
      })
      .finally(() => setLoading(false));
  };

  const resetForm = () => {
    resetFormCustomPropertyValues();
    setBatchEdit(false);
    setBatchEditItems(initialBatchEditItems);
    setTmpFieldAssociations([]);
    setDefaultSelectedTab({ tab: 'general' });
    clearSearchResults();
    setAssetsWithSameName({ attributes: [], entities: [] });
    setAttributeValues([]);
    setTmpAttributeValues([]);
    setAttributeValuesLoaded(false);

    const defaultAttributeRole = attributeRoles?.filter(
      x => x.attribute_role_name.toLowerCase() === AttributeRole.dimension.toLowerCase()
    );
    const glossaryId = data[0]?.glossary_id || currentGlossary?.glossary_id || null;

    setFormData(prev => ({
      ...initialFormState,
      // The dialog is reset after each save so another asset can be added, and the picker keeps the
      // kind the user chose rather than snapping back: picking Dimension under a Term and saving
      // used to return the picker to Term, so the next asset came out as the wrong kind. The
      // dialog's own default only applies while the picker has no value, which is the first open.
      asset_role_id:
        prev.asset_role_id ||
        (defaultObjectName === Asset.Entity
          ? Asset.Entity
          : defaultAttributeRole.length > 0 && defaultAttributeRole[0].attribute_role_id),
      glossary_id: glossaryId,
      // "Add child" keeps its parent so the next asset lands in the same place.
      parent_entity_id: mode === 'add_child' ? data[0]?.entity_id || null : null,
      entity_type_id: entityTypes.find(t => t.entity_type_is_default)?.entity_type_id || null,
      status_id: statuses.default_glossary_status_id,
      owner_principal_id: loggedInUser ? loggedInUser.principal_id : null,
      attribute_sensitivity_id: attributeSensitivities.find(x => x.is_default)
        ?.attribute_sensitivity_id,
    }));

    fetchParentEntities(glossaryId);
  };

  /**
   * The three options a user actually picks between. "Term" is not a row in attribute_role — it is a
   * synthetic option standing for the Entity table — so it is prepended here rather than seeded into
   * reference data, which keeps the roles table meaning only what it says.
   * @returns {Array<{value: any, label: string, icon: string, tooltip: string}>}
   */
  const getRoleOptions = () => {
    const roleOptions = attributeRoles.map(role => ({
      value: role.attribute_role_id,
      label: role.attribute_role_name,
      icon: role.attribute_role_name.toLowerCase(),
      secondaryLabel: AssetName[Asset.Attribute],
      // Held in attribute_role_description, so the text describing a role lives with the role rather
      // than in the GUI — as attribute_sensitivity and entity_type already do.
      tooltip: role.attribute_role_description,
    }));

    return [
      {
        value: Asset.Entity,
        label: AssetName[Asset.Entity],
        // Deliberately the fixed word rather than the glossary's default entity type: the picker is
        // choosing a kind of asset, and the option should read the same in every glossary. The type
        // itself is chosen further down the form.
        secondaryLabel: 'Term',
        icon: Asset.Entity,
        // The only option with no row of its own to hang a description on: it stands for the entity
        // table rather than for a role, so this one stays here.
        tooltip:
          'Something your information model describes, such as "Customer" or "Order".\nCan be nested, and attributes can be placed under it to describe it further.',
      },
      ...roleOptions,
    ];
  };

  /**
   * The message shown under the role picker.
   *
   * An error from the server wins — the only one that lands on this field is the refusal to turn an
   * entity that still has children into an attribute.
   *
   * Otherwise it warns only when the chosen role cannot keep something the asset actually has:
   * becoming a Term drops values and links to physical fields, since an entity has neither; and any
   * role other than Dimension drops values, since only a Dimension can hold them. A Measure does keep
   * its field links, so a plain role change never warns about those — and an asset with nothing to lose
   * gets no message at all, since the change of role is the user's intent rather than a risk.
   *
   * Deliberately not an error in either case — the loss is intended and the save goes ahead. Only a
   * single asset being edited has its values counted, so a batch role change discards them without
   * warning, the same as a batch conversion does.
   * @returns {{valid: boolean|string, message: string}|undefined}
   */
  const getRoleValidation = () => {
    if (formErrors.attribute_role_id) {
      return { valid: false, message: formErrors.attribute_role_id };
    }

    // Only an attribute has values, field links and a definition to lose, and only while being
    // edited.
    if (!isEdit || sourceObjectName !== Asset.Attribute) return undefined;

    const becomesEntity = isConversion && targetObjectName === Asset.Entity;
    const losesValues =
      attributeValues.length > 0 &&
      !canHaveAttributeValues(
        attributeRoles.find(role => role.attribute_role_id === formData.asset_role_id)
      );

    if (!becomesEntity && !losesValues) return undefined;

    const losses = [];

    // A Term has no definition, so one written on the attribute is discarded by the conversion.
    // textareaToRaw returns an empty string when the editor holds no text.
    if (becomesEntity && textareaToRaw({ value: formData.attribute_definition }) !== '') {
      losses.push('definition');
    }
    if (attributeValues.length > 0) {
      losses.push(`${attributeValues.length} value${attributeValues.length > 1 ? 's' : ''}`);
    }
    if (becomesEntity && tmpFieldAssociations.length > 0) {
      losses.push(
        `${tmpFieldAssociations.length} field association${tmpFieldAssociations.length > 1 ? 's' : ''}`
      );
    }

    // Nothing is lost, so there is nothing to warn about. Changing the role is what the user asked
    // for; only its consequences are worth a message.
    if (losses.length === 0) return undefined;

    const change = becomesEntity
      ? `change this ${AssetName[Asset.Attribute]} into a Term`
      : `change the role of this ${AssetName[Asset.Attribute]}`;

    // Reads as "a", "a and b", or "a, b and c".
    const lossList =
      losses.length > 1
        ? `${losses.slice(0, -1).join(', ')} and ${losses[losses.length - 1]}`
        : losses[0];

    return {
      valid: 'danger',
      message: `Saving will ${change} and discard its ${lossList}.`,
    };
  };

  /**
   * Lists the assets already using this name, each with the glossary it belongs to, so the user can
   * see whether the clash is the one that matters.
   * @param {object[]} assets - Normalised matches from getSameNameValidation.
   */
  const sameNameTooltip = assets => {
    return assets.map((asset, index) => (
      <React.Fragment key={index}>
        <div className="tooltip-title">
          <Icon name={asset.icon} />
          {asset.name}
          {asset.glossaryName && <span className="grey-text"> — {asset.glossaryName}</span>}
        </div>
        <div className="tooltip-body inverted-margin">{textareaToPlainText(asset.description)}</div>
      </React.Fragment>
    ));
  };

  /**
   * The message under the name field: a server-side error if there is one, otherwise the duplicate
   * name warning for the role and glossary currently chosen.
   * @returns {{valid: boolean|string, message: string, tooltipBody?: object}|undefined}
   */
  const getNameValidation = () => {
    if (formErrors.attribute_name || formErrors.entity_name) {
      return { valid: false, message: formErrors.attribute_name || formErrors.entity_name };
    }

    const validation = getSameNameValidation({
      matches: assetsWithSameName,
      glossaryId: formData.glossary_id,
      targetObjectName,
    });

    return validation && { ...validation, tooltipBody: sameNameTooltip(validation.assets) };
  };

  const getSensitivityOptions = () => {
    return formData.is_pii
      ? formatSelectOptions({
          options: attributeSensitivities.map(x => {
            return { ...x, is_not_allowed_for_pii: !x.is_allowed_for_pii };
          }),
          optionValue: 'attribute_sensitivity_id',
          optionLabel: 'attribute_sensitivity_name',
          optionDisabled: 'is_not_allowed_for_pii',
          tooltip: 'attribute_sensitivity_description',
          className: 'className',
        })
      : formatSelectOptions({
          options: attributeSensitivities,
          optionValue: 'attribute_sensitivity_id',
          optionLabel: 'attribute_sensitivity_name',
          tooltip: 'attribute_sensitivity_description',
          className: 'className',
        });
  };

  const renderAttributeSensitivityControl = () => {
    const name = 'attribute_sensitivity_id';
    const label = !batchEdit && 'Sensitivity Class';
    const value = formData.attribute_sensitivity_id;
    let options = getSensitivityOptions();
    const title = `Set the sensitivity class of this attribute.\nThis does not have any functional effect in Katalogue, it is merely a property of attributes.\nSee your organization's definition of sensitivity classification.`;
    const onChange = handleInputChange;
    const disabled = loading || (batchEdit && !batchEditItems.batch_attribute_sensitivity_id);
    if (options.length > 4) {
      return (
        <Select
          name={name}
          label={label}
          value={value}
          options={options}
          title={title}
          onChange={onChange}
          disabled={disabled}
          sortOptions={false}
        />
      );
    } else {
      return (
        <SelectButtons
          name={name}
          label={label}
          value={value}
          options={options}
          title={title}
          onChange={onChange}
          disabled={disabled}
        />
      );
    }
  };

  return (
    <div className="form">
      <form onSubmit={onSubmit} ref={ref}>
        <div className="form-block vertical">
          <InputStatus
            name="asset_name"
            label="Name"
            displayNameCode={sharedFieldCode('attribute_name')}
            value={formData.asset_name}
            statusValue={formData.status_id}
            assetCategory="glossary"
            onChange={handleInputChange}
            loading={loading}
            batchEdit={batchEdit}
            batchEditItems={batchEditItems}
            validation={getNameValidation()}
          />
        </div>

        {/* The role decides which kind of asset this is, and therefore which of the properties
            below apply, so it sits above the tabs rather than inside one. */}
        <div className="form-block vertical">
          {batchEdit && (
            <label>
              <Checkbox
                checked={batchEditItems.batch_asset_role_id}
                name="batch_asset_role_id"
                label="Edit Role"
                onChange={e => handleInputChange(e, 'batch')}
                disabled={loading}
              />
            </label>
          )}
          <SelectButtons
            name="asset_role_id"
            label={!batchEdit && 'Role'}
            displayNameCode="attribute_role_id"
            value={formData.asset_role_id}
            options={getRoleOptions()}
            // No title: Label reads the display_name row named by displayNameCode above, showing
            // its description and then its instruction. The text is therefore editable as data.
            onChange={handleInputChange}
            disabled={loading || (batchEdit && !batchEditItems.batch_asset_role_id)}
            validation={getRoleValidation()}
          />
        </div>

        {/* An Entity Type only labels a Term, and only exists once its glossary has some. */}
        {isEntity && entityTypes.length > 0 && (
          <div className="form-block vertical">
            {batchEdit && (
              <label>
                <Checkbox
                  checked={batchEditItems.batch_entity_type_id}
                  name="batch_entity_type_id"
                  label="Edit Type"
                  onChange={e => handleInputChange(e, 'batch')}
                  disabled={loading}
                />
              </label>
            )}
            <Select
              name="entity_type_id"
              label={!batchEdit && 'Type'}
              value={formData.entity_type_id === 'multiple' ? undefined : formData.entity_type_id}
              options={formatSelectOptions({
                options: entityTypes,
                optionValue: 'entity_type_id',
                optionLabel: 'entity_type_name',
                tooltip: 'entity_type_description',
              })}
              onChange={handleInputChange}
              placeholder={
                formData.entity_type_id === 'multiple' ? '< Multiple >' : 'Select type...'
              }
              disabled={loading || (batchEdit && !batchEditItems.batch_entity_type_id)}
              isNullable={true}
              isOptional={true}
            />
          </div>
        )}

        <FormTabs
          defaultSelectedTab={defaultSelectedTab}
          formErrors={formErrors}
          hideTabsIfOnlyOne={true}
        >
          <div label="General">
            {!batchEdit && (
              <div className="form-block vertical">
                {
                  <Textarea
                    name="asset_description"
                    displayNameCode={sharedFieldCode('attribute_description')}
                    value={formData.asset_description}
                    onChange={handleInputChange}
                    disabled={loading}
                    isOptional={true}
                  />
                }
              </div>
            )}
            {/* A definition says how the values an attribute describes are determined, which a Term
                has none of. */}
            {!batchEdit && !isEntity && (
              <div className="form-block vertical">
                <Textarea
                  name="attribute_definition"
                  value={formData.attribute_definition}
                  onChange={handleInputChange}
                  disabled={loading}
                  isOptional={true}
                />
              </div>
            )}

            {/* Both kinds live in a glossary, so the picker is shared. */}
            <div className="form-block vertical">
              {batchEdit && (
                <label>
                  <Checkbox
                    checked={batchEditItems.batch_glossary_id}
                    name="batch_glossary_id"
                    label="Edit Glossary"
                    onChange={e => handleInputChange(e, 'batch')}
                    disabled={loading}
                  />
                </label>
              )}
              <Select
                name="glossary_id"
                label={!batchEdit && 'Glossary'}
                value={formData.glossary_id === 'multiple' ? undefined : formData.glossary_id}
                options={formatSelectOptions({
                  options: glossaries,
                  optionValue: 'glossary_id',
                  optionLabel: 'glossary_name',
                })}
                onChange={handleInputChange}
                placeholder={formData.glossary_id === 'multiple' ? '< Multiple >' : ''}
                disabled={loading || (batchEdit && !batchEditItems.batch_glossary_id)}
              />
            </div>

            {/* Only entities can have children, so the options here are entities whichever kind is
                being edited — an attribute can sit under one but never hold one. */}
            <div className="form-block vertical">
              {batchEdit && (
                <label>
                  <Checkbox
                    checked={batchEditItems.batch_parent_entity_id}
                    name="batch_parent_entity_id"
                    label="Edit Parent"
                    onChange={e => handleInputChange(e, 'batch')}
                    disabled={loading}
                  />
                </label>
              )}
              <Select
                name="parent_entity_id"
                label={!batchEdit && 'Parent'}
                value={
                  formData.parent_entity_id === 'multiple' ? undefined : formData.parent_entity_id
                }
                options={formatSelectOptions({
                  options: entities,
                  optionValue: 'entity_id',
                  optionLabel: 'entity_name',
                  optionSecondaryLabel: 'entity_path',
                })}
                onChange={handleInputChange}
                placeholder={
                  formData.parent_entity_id === 'multiple' ? '< Multiple >' : 'Select parent...'
                }
                disabled={loading || (batchEdit && !batchEditItems.batch_parent_entity_id)}
                isNullable={true}
                isOptional={true}
              />
            </div>

            {/* A unit measures the values an attribute describes, so it belongs to a Measure. A
                Dimension is enumerated and carries Attribute Values instead, and a Term has
                neither. */}
            {showAttributeUnit && (
              <div className="form-block vertical">
                {batchEdit && (
                  <label>
                    <Checkbox
                      checked={batchEditItems.batch_attribute_unit_id}
                      name="batch_attribute_unit_id"
                      label="Edit Attribute Unit"
                      onChange={e => handleInputChange(e, 'batch')}
                      disabled={loading}
                    />
                  </label>
                )}
                <Select
                  name="attribute_unit_id"
                  label={!batchEdit}
                  value={
                    formData.attribute_unit_id === 'multiple'
                      ? undefined
                      : formData.attribute_unit_id
                  }
                  options={formatSelectOptions({
                    options: attributeUnits,
                    optionValue: 'attribute_unit_id',
                    optionLabel: 'attribute_unit_code',
                    optionSecondaryLabel: 'attribute_unit_name',
                  })}
                  isNullable={true}
                  onChange={handleInputChange}
                  placeholder={
                    formData.attribute_unit_id === 'multiple'
                      ? '< Multiple >'
                      : 'Select attribute unit...'
                  }
                  disabled={loading || (batchEdit && !batchEditItems.batch_attribute_unit_id)}
                  isOptional={true}
                />
              </div>
            )}

            <div className="form-block vertical">
              {batchEdit && (
                <label>
                  <Checkbox
                    checked={batchEditItems.batch_owner_principal_id}
                    name="batch_owner_principal_id"
                    label="Edit Owner"
                    onChange={e => handleInputChange(e, 'batch')}
                    disabled={loading}
                  />
                </label>
              )}

              <SelectOwner
                name="owner_principal_id"
                value={
                  formData.owner_principal_id === 'multiple'
                    ? undefined
                    : formData.owner_principal_id
                }
                label={!batchEdit}
                placeholder={formData.owner_principal_id === 'multiple' ? '< Multiple >' : ''}
                onChange={handleInputChange}
                disabled={loading || (batchEdit && !batchEditItems.batch_owner_principal_id)}
              />
            </div>
          </div>

          {/* Sensitivity and PII describe the data an attribute stands for. A Term holds no data, so
              the tab is left out entirely — FormTabs skips falsy children. */}
          {!isEntity && (
            <div label="Sensitivity">
              <div className="form-block vertical">
                {batchEdit && (
                  <label>
                    <Checkbox
                      checked={batchEditItems.batch_attribute_sensitivity_id}
                      name="batch_attribute_sensitivity_id"
                      label="Edit Sensitivity Class"
                      onChange={e => handleInputChange(e, 'batch')}
                      disabled={loading}
                    />
                  </label>
                )}
                {renderAttributeSensitivityControl()}
              </div>

              <div className="form-block vertical">
                {batchEdit ? (
                  <label>
                    <Checkbox
                      checked={batchEditItems.batch_is_pii}
                      name="batch_is_pii"
                      label="Edit Personally Identifiable Information (PII)"
                      onChange={e => handleInputChange(e, 'batch')}
                      disabled={loading}
                    />
                  </label>
                ) : null}

                <Checkbox
                  name="is_pii"
                  checked={formData.is_pii}
                  onChange={handleInputChange}
                  label="Personally Identifiable Information (PII)"
                  title={`Tag this attribute as PII.\nThis tag will be propagated to all fields associated with this attribute, and aggregated to the dataset level.\nThis does not have any other functional effect in Katalogue, it is merely a property of attributes.\nSee your organization's definition of what should be considered PII or not.`}
                  disabled={loading || (batchEdit && !batchEditItems.batch_is_pii)}
                />
              </div>
            </div>
          )}

          {/* Only a Dimension is enumerated, so only a Dimension has values to list. */}
          {!batchEdit && !isEntity && isDimension && (
            <div
              label="Values"
              // The values that will exist after a save: the ones on their way out are not counted,
              // and they are listed under their own heading in the tab itself.
              resultCount={tmpAttributeValues.filter(value => !value.isDeleted).length}
            >
              <AttributeValuesForm
                // The list renders its own inputs, so the tab strip cannot see their names to work
                // out that an error belongs here. This names the part of the payload the component
                // owns, which is what the keys of those errors are prefixed with.
                name="attribute_values"
                values={tmpAttributeValues}
                original={attributeValues}
                onChange={setTmpAttributeValues}
                loading={loading}
                formErrors={formErrors}
              />
            </div>
          )}

          {/* Only an attribute is bound to physical fields. */}
          {!batchEdit && !isEntity && (
            <div label="Fields" resultCount={tmpFieldAssociations.length}>
              <div className="form-block vertical">
                <Select
                  name="field_id"
                  value={tmpFieldAssociations.map(x => {
                    return { value: x.field_id, label: x.field_name };
                  })}
                  label={false}
                  options={requestResults}
                  onInputChange={handleSearchFieldInputChange}
                  loadMoreOptions={loadNextPage}
                  loading={loadingMoreOptions}
                  resultCount={searchResultsCount && searchResultsCount.field}
                  searchTerm={searchTerm}
                  showSearchResultCount={true}
                  onSelectAll={handleSelectAll}
                  clearSearchResults={clearSearchResults}
                  placeholder="Search Fields to add..."
                  isMulti={true}
                  isClearable={true}
                  showMultiSelectedElements={false}
                  onChange={addFieldAssociation}
                  disabled={loading}
                />
              </div>

              <div className="form-block vertical">
                <label className="form-strong-text">
                  {tmpFieldAssociations.length} Associated Fields
                </label>
                {tmpFieldAssociations.map((item, index) => {
                  return (
                    <div
                      className={'side-dialog-search-result' + (loading ? ' disabled' : '')}
                      key={index}
                      onClick={() => {
                        !loading && removeFieldAssociation(item);
                      }}
                    >
                      <div className="side-dialog-search-result-icon">
                        <Icon name={item.icon} />
                      </div>
                      <div className="side-dialog-search-result-body">
                        <div className="side-dialog-search-result-title">{item.field_name}</div>
                        <div className="side-dialog-search-result-subtitle">
                          {item.secondaryLabel}
                        </div>
                      </div>
                      {item.isNew && <div className="side-dialog-search-result-is-new"></div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {hasCustomProperties && (
            <div label="More">
              <CustomPropertiesForm
                customProperties={customProperties}
                formData={formCustomPropertyValues}
                batchEdit={batchEditFormCustomPropertyValues}
                batchEditItems={batchEditItemsFormCustomPropertyValues}
                onChange={setFormCustomPropertyValues}
                loading={loading}
              />
            </div>
          )}
        </FormTabs>
      </form>
    </div>
  );
};

export const AddGlossaryAssetForm = forwardRef(_AddGlossaryAssetForm);
